// ============================================================================
// Quote preview. Run B Phase 5, 2026-08-18.
//
// What a client would see, from the project's own service lines, laid out the
// way QUOTE TEMPLATE.docx lays it out.
//
// This is a PREVIEW and nothing else. There is no send, no e-sign, no PDF, and
// no quotes table — this run did not create one, so nothing rendered here is a
// record that anything was ever quoted to anybody. The screen says so, at the
// top, where the person reading it will see it before they assume otherwise.
//
// The lines come from v_project_quote_lines, which applies the SAME
// itemised-or-lump rule as both invoice builders. A quote and the invoice that
// follows it therefore cannot disagree about how the work is described or what
// it adds up to, and the migration that created the view fails if any project
// quotes a total that differs from its bill_amount.
// ============================================================================
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { supabase } from '../../../shared/supabase';
import { ErrorState } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';

interface QuoteLine {
  line_order: number | null;
  description: string | null;
  amount: number | null;
  itemised: boolean | null;
}

interface QuoteHeader {
  project_id: string;
  facility_name: string | null;
  account_name: string | null;
  stage: string | null;
  start_date: string | null;
  facilities: {
    facility_name: string | null;
    facility_address: string | null;
    state: string | null;
    billing_contact_name: string | null;
    billing_contact_email: string | null;
  } | null;
}

const money = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const longDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

export default function QuotePreview() {
  const { projectId } = useParams<{ projectId: string }>();
  const [header, setHeader] = useState<QuoteHeader | null>(null);
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [h, l] = await Promise.all([
          supabase
            .from('projects')
            .select(
              `project_id, facility_name, account_name, stage, start_date,
               facilities ( facility_name, facility_address, state, billing_contact_name, billing_contact_email )`,
            )
            .eq('project_id', projectId)
            .maybeSingle(),
          supabase
            .from('v_project_quote_lines')
            .select('line_order, description, amount, itemised')
            .eq('project_id', projectId)
            .order('line_order'),
        ]);
        if (cancelled) return;
        if (h.error) throw h.error;
        if (l.error) throw l.error;
        setHeader((h.data ?? null) as unknown as QuoteHeader | null);
        setLines((l.data ?? []) as unknown as QuoteLine[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to build the quote preview.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const total = lines.reduce((sum, l) => sum + Number(l.amount ?? 0), 0);
  const itemised = lines.length > 0 && lines[0].itemised === true;
  const facility = header?.facilities ?? null;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-3">
        <Link
          to={`/project-work/${projectId}`}
          className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the project
        </Link>
      </div>

      <p className="mb-4 flex items-start gap-2 rounded-lg border border-st-warn-border bg-st-warn-bg px-3 py-2.5 text-[12.5px] text-st-warn-fg">
        <FileText className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          <strong>Preview only.</strong> Nothing here has been sent, signed or saved as a quote —
          there is no quotes table yet, so this is a rendering of what the project already says, not
          a record that anybody was ever quoted it. Sending, signing and keeping a copy are a later
          piece of work.
        </span>
      </p>

      {error && <ErrorState title="Couldn't build the preview" body={error} />}
      {isLoading && <p className="text-sm text-tcs-text-secondary">Loading…</p>}

      {!isLoading && !error && !header && (
        <ErrorState title="No such project" body={`Nothing found for ${projectId}.`} />
      )}

      {!isLoading && !error && header && (
        <>
          <div className="rounded-xl border border-tcs-border bg-tcs-surface p-8">
            <h1 className="text-2xl font-semibold tracking-wide text-tcs-text">QUOTE</h1>

            <p className="mt-6 text-[13px] text-tcs-text">
              <span className="text-tcs-text-muted">DATE:</span> {longDate(today)}
            </p>

            <div className="mt-6 text-[13px] leading-relaxed text-tcs-text">
              <p className="text-tcs-text-muted">TO:</p>
              {facility?.billing_contact_name && <p>{facility.billing_contact_name}</p>}
              <p className="mt-2">{facility?.facility_name ?? header.facility_name ?? '—'}</p>
              {facility?.facility_address && <p>{facility.facility_address}</p>}
              {facility?.state && !facility?.facility_address && <p>{facility.state}</p>}
            </div>

            <p className="mt-6 text-[13px] text-tcs-text">
              Terms: Net 30&nbsp;&nbsp;&nbsp;Due Date: TBD
            </p>

            <table className="mt-6 w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-tcs-border text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                  <th className="py-2 font-medium">Description</th>
                  <th className="w-32 py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-tcs-border">
                    <td className="py-2.5 pr-4 text-tcs-text">{l.description ?? '—'}</td>
                    <td className="py-2.5 text-right font-mono text-tcs-text">{money(l.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="py-2.5 pr-4 font-semibold text-tcs-text">TOTAL</td>
                  <td className="py-2.5 text-right font-mono font-semibold text-tcs-text">
                    {money(total)}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="mt-8 border-t border-tcs-border pt-4 text-[12px] text-tcs-text-secondary">
              <p>Make all checks payable to: Terminal Cleaning Services</p>
              <p>
                If you have any questions concerning this quote, contact
                bswartz@terminalcleaningservices.com
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-tcs-border bg-tcs-field px-3 py-2.5 text-[12px] text-tcs-text-secondary">
            <p>
              {itemised
                ? 'Itemised: every service line carries a rate and they sum exactly to what the project says to bill, so the client sees the work broken out.'
                : 'Not itemised: a line has no rate, or the line rates do not sum to what the project says to bill. The client sees the project total on one line naming every service performed — the total can never drift from the amount the project says to bill.'}
            </p>
            <p className="mt-1">
              Wording comes from the service catalog's client-facing names, the same source the
              invoice uses, so a quote and the invoice that follows it cannot disagree.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
